import type { Ref } from 'vue'
import type { EventDateType } from '~/composables/types/event'
import {
  useDateRequestFormStorage,
  type DateRequestFormData,
} from '~/composables/useDateRequestFormStorage'

export interface DateRequestFormModel {
  email: string
  phone: string
  name: string
  surname: string
  numberOfTickets: number | null
  companyName: string
  city: string
  dateType: EventDateType | null
  dates: string[]
  additionalInfo?: string
}

const createEmptyFormModel = (): DateRequestFormModel => ({
  email: '',
  phone: '',
  name: '',
  surname: '',
  numberOfTickets: null,
  companyName: '',
  city: '',
  dateType: null,
  dates: [],
  additionalInfo: '',
})

const normalizeDateType = (
  value: DateRequestFormData['dateType']
): EventDateType | null =>
  value === 'PRIVATE' || value === 'PUBLIC' ? value : null

/**
 * Owns the date-request form ref and a single sessionStorage hydration path.
 * Ticket count is never restored from persisted data (see applySavedData); buy-box
 * suggested count is re-applied after every hydration (see applySuggestedTicketCount).
 */
export const useDateRequestFormState = (): {
  form: Ref<DateRequestFormModel>
} => {
  const form = ref<DateRequestFormModel>(createEmptyFormModel())

  const { savedFormData, loaded } = useDateRequestFormStorage()
  const { dateRequestSuggestedTicketCount } = useUiState()
  const eventData = useEventData()

  const applySavedData = (data: DateRequestFormData) => {
    form.value = {
      ...createEmptyFormModel(),
      ...data,
      numberOfTickets: null,
      dateType: normalizeDateType(data.dateType),
      dates: [...(data.dates ?? [])],
    }
  }

  const applySuggestedTicketCount = () => {
    const suggested = dateRequestSuggestedTicketCount.value
    if (suggested == null || suggested < 1) {
      return
    }
    form.value.numberOfTickets = suggested
  }

  // Single hydration: fires once when session storage finishes loading, and
  // again only if `savedFormData` is reassigned (no deep watch).
  watch(
    [loaded, savedFormData, dateRequestSuggestedTicketCount, eventData],
    ([isLoaded, data]) => {
      if (!isLoaded || !data) {
        return
      }
      applySavedData(data)
      applySuggestedTicketCount()
    },
    { immediate: true }
  )

  return {
    form,
  }
}
